import { useContext, useEffect, useMemo, useState } from 'react';
import ListAndDetailsSubLayout from '@layouts/ListAndDetailsSubLayout';
import { CalendarPlus } from '@phosphor-icons/react';
import useIsMobile, { SIZES } from '@hooks/useIsMobile';
import TeamsContext from '@/providers/Teams/TeamsContext';
import DashboardContext from '@layouts/DashboardLayout/DashboardContext';
import useNoodleApi from '@/hooks/useNoodleApi';
import * as tsClient from '@tsClient';
import useBookings from './useBookings';

const PAGE_SIZE = 10;

const Bookings: React.FC = ({ children }) => {
  const [arePast, setArePast] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [pastPage, setPastPage] = useState(1);
  const [upcomingPage, setUpcomingPage] = useState(1);

  const { currentTeamOwner } = useContext(TeamsContext);
  const { getData: getTeamMembersFn, data: teamMembers } = useNoodleApi(tsClient.creators.getCreatorTeam);

  const { setInitiateBookingModalOpen, setBrowserTabTitle } = useContext(DashboardContext);

  useEffect(() => {
    if (currentTeamOwner) {
      getTeamMembersFn({ creatorId: currentTeamOwner.id, perPage: 1_000 });
    }
  }, [currentTeamOwner, getTeamMembersFn]);

  useEffect(() => {
    if (arePast) {
      setCurrentPage(pastPage);
    } else {
      setCurrentPage(upcomingPage);
    }
  }, [arePast, pastPage, upcomingPage]);

  const isOneColumn = useIsMobile(SIZES.md);

  const bookingsData = useBookings({
    arePast,
    creatorId: currentTeamOwner?.id,
    memberId: selectedFilter,
    page: currentPage,
    perPage: PAGE_SIZE,
  });

  const header = useMemo(
    () => ({
      actions: arePast
        ? []
        : [
          {
            icon: CalendarPlus,
            isPrimary: true,
            label: 'Initiate Booking',
            onClick: () => setInitiateBookingModalOpen(true),
          },
        ],
      tabs: [
        {
          label: 'Upcoming',
          onClick: () => setArePast(false),
          selected: !arePast,
        },
        {
          label: 'Past',
          onClick: () => setArePast(true),
          selected: arePast,
        },
      ],
      title: 'Bookings',
    }),
    [setInitiateBookingModalOpen, arePast],
  );

  const teamMemberFilter = useMemo(() => {
    if (!teamMembers) return [];

    return [
      {
        isChecked: selectedFilter === 'all',
        key: 'all',
        label: 'All',
      },
      ...teamMembers.items.map(member => ({
        isChecked: selectedFilter === member.id,
        key: member.id,
        label: member.name || member.email || member.id,
      })),
    ];
  }, [teamMembers, selectedFilter]);

  useEffect(() => {
    setBrowserTabTitle('Bookings');
  }, [setBrowserTabTitle]);

  if (!bookingsData) return null;

  const setCurrentPageFn = (page: number):void => {
    if (arePast) {
      setPastPage(page);
    } else {
      setUpcomingPage(page);
    }
  };

  if (!bookingsData) return null;

  const handlePageChange = (newPage: number) :void => {
    setCurrentPageFn(newPage);
  };

  return (
    <ListAndDetailsSubLayout
      header={header}
      filters={{
        items: [
          {
            isMultiSelect: false,
            items: teamMemberFilter,
            label: 'Team member',
            onChange: setSelectedFilter,
            selected: selectedFilter,
          },
        ],
      }}
      isOneColumn={isOneColumn}
      resourceType="bookings"
      pagination={{
        numPages: Math.ceil(bookingsData.numItems ? bookingsData.numItems / PAGE_SIZE : 1),
        onChangePage: handlePageChange,
        page: currentPage,
      }}
      {...bookingsData}
      reloadListView={bookingsData.reloadList}
    >
      {children}
    </ListAndDetailsSubLayout>
  );
};

export default Bookings;
